import Icon from '@components/v2/ui/Icon/Icon';
import IconButton from '@components/v2/ui/IconButton/IconButton';
import Typography from '@components/v2/ui/Typography/Typography';
import { Box } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import Fade from '@mui/material/Fade';
import Slide from '@mui/material/Slide';
import { TransitionProps } from '@mui/material/transitions';
import { ChevronLeft, ChevronRight, CloudDownload, Image, X } from 'lucide-react';
import * as React from 'react';
import LangEnum from '../../../lang/LangEnum';
import MediaRenderer from '../MediaRenderer';
import { MediaType } from '../mimeType';
import {
  getArrowButtonStyles,
  getHeaderRowIconsStyles,
  useMediasPreviewStyles,
} from './MediasPreview.styles';
import useMediaPreview from './useMediasPreview';

export interface MediasPreviewItem {
  url: string;
  type: MediaType;
  fileName: string;
}

interface MediasPreviewProps<T extends MediasPreviewItem | undefined> {
  open: boolean;
  onClose: () => void;
  medias?: T[];
  selectedMedia?: T;
}

const Transition = React.forwardRef(
  (props: TransitionProps & { children: React.ReactElement }, ref: React.Ref<unknown>) => (
    <Slide direction="up" ref={ref} {...props} />
  ),
);

const MediasPreview = <T extends MediasPreviewItem>({
  open,
  medias,
  onClose,
  selectedMedia,
}: MediasPreviewProps<T>) => {
  const { classes } = useMediasPreviewStyles();
  const {
    timeout,
    translate,
    currentMedia,
    selectedIndex,
    currentMediaUrl,
    handleOnClickToDownload,
    handleNavigateDirection,
  } = useMediaPreview(medias, selectedMedia);

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      disableScrollLock={false}
      slots={{
        transition: Transition,
      }}
      slotProps={{
        paper: {
          className: classes.dialogPaperProps,
        },
      }}
    >
      {currentMedia && (
        <Fade in timeout={timeout} key={selectedIndex}>
          <div className={classes.mediaContainer}>
            <MediaRenderer
              autoPlay
              aspectRatio="60%"
              mediaContentContain
              src={currentMediaUrl}
              type={currentMedia.type}
            />
          </div>
        </Fade>
      )}
      <Box sx={getHeaderRowIconsStyles('left')}>
        <Icon icon={Image} />
        <Typography variant="h6" className={classes.textOverflow}>
          {currentMedia?.fileName}
        </Typography>
      </Box>
      <Box sx={getHeaderRowIconsStyles('right')}>
        <IconButton
          icon={CloudDownload}
          size="large"
          ariaLabel={translate(LangEnum.GLOBAL_DOWNLOAD)}
          sx={{ color: 'primary.contrastText' }}
          onClick={() => {
            if (currentMedia) {
              handleOnClickToDownload(currentMedia);
            }
          }}
        />
        <IconButton
          icon={X}
          size="large"
          ariaLabel={translate(LangEnum.GLOBAL_CLOSE)}
          onClick={onClose}
          sx={{ color: 'primary.contrastText' }}
        />
      </Box>
      <IconButton
        size="large"
        icon={ChevronLeft}
        ariaLabel={translate(LangEnum.GLOBAL_PREVIOUS)}
        sx={getArrowButtonStyles('left')}
        onClick={() => handleNavigateDirection(-1)}
      />
      <IconButton
        size="large"
        icon={ChevronRight}
        ariaLabel={translate(LangEnum.GLOBAL_NEXT)}
        sx={getArrowButtonStyles('right')}
        onClick={() => handleNavigateDirection(1)}
      />
    </Dialog>
  );
};

export default MediasPreview;
